import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { createTemplate, getTemplate, listTemplates } from './api';

/**
 * Onboarding templates.
 *
 * A template is a list of steps and how many days after the start each is due. That is the whole
 * model, deliberately: the temptation is to add conditions, branches and dependencies, and what a CS
 * team actually needs is the same eight things done in the same order for every new customer.
 *
 * Editing a template never touches a plan already running. A plan is somebody's real list of work
 * with tasks assigned against it, and rewriting it from under them would delete work in progress.
 */
export function TemplatesPage(): React.JSX.Element {
  const client = useQueryClient();

  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [steps, setSteps] = useState('');
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['cs-templates'], queryFn: listTemplates });

  const detail = useQuery({
    queryKey: ['cs-template', open],
    queryFn: () => getTemplate(open!),
    enabled: open !== null,
  });

  const create = useMutation({
    mutationFn: () =>
      createTemplate({
        name: name.trim(),
        steps: parseSteps(steps),
      }),
    onSuccess: () => {
      setCreating(false);
      setName('');
      setSteps('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['cs-templates'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const parsed = parseSteps(steps);

  return (
    <div className="view">
      <PageHeader
        title={t('cs.templates')}
        description={t('cs.templatesDescription')}
        actions={
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() => setCreating((current) => !current)}
          >
            {creating ? t('confirm.cancel') : t('cs.newTemplate')}
          </button>
        }
      />

      {creating && (
        <Panel title={t('cs.newTemplate')}>
          <label className="stack__field">
            <span>
              {t('cs.templateName')} <span className="required">*</span>
            </span>
            <input
              value={name}
              maxLength={120}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <label className="stack__field">
            <span>{t('cs.templateSteps')}</span>
            {/*
              One step per line, with an optional "@ n" for the day it is due. A form with a
              repeating fieldset of three inputs is slower to fill than typing eight lines, and
              typing eight lines is what somebody with the list in front of them wants to do.
            */}
            <textarea
              rows={8}
              value={steps}
              placeholder={t('cs.templateStepsPlaceholder')}
              onChange={(event) => setSteps(event.target.value)}
            />
            <span className="muted small">{t('cs.templateStepsHint')}</span>
          </label>

          {parsed.length > 0 && (
            <ol className="plan">
              {parsed.map((step, index) => (
                <li key={`${step.title}-${index}`} className="plan__step">
                  <span className="plan__mark" aria-hidden="true">
                    ○
                  </span>
                  <span className="cell-stack">
                    <span>{step.title}</span>
                    <span className="muted small">
                      {t('cs.dueOnDay').replace('{n}', String(step.offsetDays))}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          )}

          {error && (
            <p className="alert small" role="alert">
              {error}
            </p>
          )}

          <div className="confirm__actions">
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={name.trim() === '' || parsed.length === 0 || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? t('work.creating') : t('cs.saveTemplate')}
            </button>
          </div>
        </Panel>
      )}

      <Panel>
        {templates.isLoading ? (
          <Loading />
        ) : templates.error ? (
          <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
        ) : (templates.data?.templates.length ?? 0) === 0 ? (
          <p className="muted">{t('cs.noTemplates')}</p>
        ) : (
          <ul className="home-list">
            {(templates.data?.templates ?? []).map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() =>
                    setOpen((current) => (current === template.id ? null : template.id))
                  }
                >
                  {template.name}
                </button>
                <span className="muted small">
                  {t('cs.stepCount').replace('{n}', String(template.stepCount))}
                </span>
              </li>
            ))}
          </ul>
        )}

        {open && detail.data?.steps && (
          <ol className="plan">
            {detail.data.steps.map((step) => (
              <li key={step.id} className="plan__step">
                <span className="plan__mark" aria-hidden="true">
                  ○
                </span>
                <span className="cell-stack">
                  <span>{step.title}</span>
                  <span className="muted small">
                    {t('cs.dueOnDay').replace('{n}', String(step.offsetDays))}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </Panel>
    </div>
  );
}

/**
 * Turns typed lines into steps.
 *
 * `Reunião de boas-vindas @ 0` — the title, then optionally the day it is due. A line with no `@`
 * falls three days after the one before it, which is a reasonable default and easy to override.
 *
 * Exported for its test: the parsing is the only logic on this screen, and getting "@ 10" wrong
 * silently means every step in a plan is due on day zero.
 */
export function parseSteps(text: string): { title: string; offsetDays: number }[] {
  const steps: { title: string; offsetDays: number }[] = [];

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (trimmed === '') continue;

    const match = /^(.*?)\s*@\s*(\d{1,4})\s*$/.exec(trimmed);
    const title = (match?.[1] ?? trimmed).trim();

    if (title === '') continue;

    const offsetDays =
      match?.[2] !== undefined ? Number(match[2]) : steps.length === 0 ? 0 : steps.length * 3;

    steps.push({ title: title.slice(0, 300), offsetDays });
  }

  return steps;
}
